import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import "./weatherChip.css";
import { WeatherIcon } from "./weatherIcons";
import { useWeatherCycle } from "./useWeatherCycle";
import {
  useWeatherDisplayMode,
  setWeatherDisplayMode,
  WEATHER_DISPLAY_MODES,
} from "./useWeatherDisplayMode";

// Centralized weather chip for the weather-by-zone feature (2026-06-22).
// Reused on the React week grid (per job) and the Create/Edit Calendar Event
// modals (per selected client). Resting state CYCLES rain% -> temp -> wind on a
// single shared ~2.6s ticker; on hover it freezes the cycle and shows a popover
// info window with all fields at once plus a freshness "as of {time}" line.
//
// Degradation (plan): no `day` -> render nothing (caller usually guards too).
// Null precipProb/windMph -> "--", never "0%". A day with no usable numbers and
// no condition renders nothing.

const fmtTemp = (v) => (v == null || v === "" ? null : `${parseInt(v, 10)}°`);

const fmtPct = (v) => (v == null || v === "" ? "--" : `${parseInt(v, 10)}%`);

const fmtWind = (v) => (v == null || v === "" ? "--" : `${parseInt(v, 10)} mph`);

// "as of 6:04a" from an epoch-ms String, in Central time.
const fmtFreshness = (fetchedMs) => {
  if (!fetchedMs) return null;
  const ms = parseInt(fetchedMs, 10);
  if (Number.isNaN(ms)) return null;
  const s = new Date(ms).toLocaleTimeString("en-US", {
    timeZone: "America/Chicago",
    hour: "numeric",
    minute: "2-digit",
  });
  // "6:04 AM" -> "6:04a"
  return s.replace(/\s?AM$/i, "a").replace(/\s?PM$/i, "p");
};

const WeatherChip = ({ day, fetchedMs, iconOnly = false, size = 14, className = "" }) => {
  const [hovered, setHovered] = useState(false);
  // Viewport coords of the chip, captured on hover so the popover can be
  // PORTALED to <body> (escapes the week-grid block's overflow:hidden and the
  // neighboring-block stacking that otherwise clipped/hid it).
  const [anchor, setAnchor] = useState(null);
  const chipRef = useRef(null);
  // Display-mode preference: 'cycle' rotates rain%->temp->wind; the others PIN
  // the resting value to one metric. The shared ticker is still subscribed
  // unconditionally (hooks rule) but its index is only consumed in cycle mode.
  const mode = useWeatherDisplayMode();
  const cycleIdx = useWeatherCycle(hovered);

  const showPopover = () => {
    const el = chipRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setAnchor({ left: r.left + r.width / 2, top: r.top - 6 });
    }
    setHovered(true);
  };
  const hidePopover = () => {
    setHovered(false);
    setAnchor(null);
  };

  // The chip IS the display-mode control: clicking it advances the global
  // preference cycle -> rain -> temp -> wind -> cycle. stopPropagation +
  // preventDefault so the click never bubbles to the surface underneath (e.g.
  // a week-grid block's open-detail handler).
  const handleChipClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const order = WEATHER_DISPLAY_MODES.map((m) => m.value);
    const idx = order.indexOf(mode);
    setWeatherDisplayMode(order[(idx + 1) % order.length]);
  };
  const handleChipKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      handleChipClick(e);
    }
  };

  if (!day) return null;

  const condition = day.condition || null;
  const rainStr = fmtPct(day.precipProb);
  const tempStr = fmtTemp(day.tempHi);
  const windStr = fmtWind(day.windMph);

  // Nothing meaningful to show — no condition glyph and no numbers at all.
  const hasAnyNumber =
    (day.precipProb != null && day.precipProb !== "") ||
    (day.tempHi != null && day.tempHi !== "") ||
    (day.windMph != null && day.windMph !== "");
  if (!condition && !hasAnyNumber) return null;

  // Cycle text: rain% -> temp -> wind. Temp may be absent; fall back to rain.
  const cycleValues = [rainStr, tempStr || rainStr, windStr];
  // In 'cycle' mode the value rotates with the shared ticker; otherwise it's
  // PINNED to the selected metric (temp falls back to rain when absent, same
  // as the cycle slot). The condition glyph always shows regardless of mode.
  const pinned = { rain: rainStr, temp: tempStr || rainStr, wind: windStr };
  const cycleText = mode === "cycle" ? cycleValues[cycleIdx] : pinned[mode];

  const freshness = fmtFreshness(fetchedMs);

  return (
    <span
      ref={chipRef}
      className={`weather-chip${iconOnly ? " weather-chip--icon-only" : ""} ${className}`.trim()}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onClick={handleChipClick}
      onKeyDown={handleChipKeyDown}
      role="button"
      tabIndex={0}
      title="Click to change weather display"
    >
      <span className={`weather-chip-glyph weather-chip-glyph--${condition || "cloudy"}`}>
        <WeatherIcon condition={condition} size={size} />
      </span>
      {!iconOnly && (
        <span className="weather-chip-cycle" aria-hidden="true">
          {cycleText}
        </span>
      )}

      {hovered &&
        anchor &&
        createPortal(
          <span
            className="weather-chip-popover bdr-border-std bdr-shadow-depth"
            style={{ left: anchor.left, top: anchor.top }}
          >
            <span className={`weather-chip-popover-row${mode === "rain" ? " is-active" : ""}`}>
              <span className="weather-chip-popover-label">Rain</span>
              <span className="weather-chip-popover-val">{rainStr}</span>
            </span>
            <span className={`weather-chip-popover-row${mode === "temp" ? " is-active" : ""}`}>
              <span className="weather-chip-popover-label">Temp</span>
              <span className="weather-chip-popover-val">{tempStr || "--"}</span>
            </span>
            <span className={`weather-chip-popover-row${mode === "wind" ? " is-active" : ""}`}>
              <span className="weather-chip-popover-label">Wind</span>
              <span className="weather-chip-popover-val">{windStr}</span>
            </span>
            <span className="weather-chip-popover-area">Based on the client's area</span>
            {freshness && (
              <span className="weather-chip-popover-fresh">as of {freshness}</span>
            )}
            <span className="weather-chip-popover-hint">
              {mode === "cycle"
                ? "Cycling · click to focus a metric"
                : "Click to change display"}
            </span>
          </span>,
          document.body
        )}
    </span>
  );
};

export default WeatherChip;
